import React, { useState, useEffect, useMemo } from 'react';
import { usePolling } from 'hooks/usePolling';
import { useEventListener } from 'hooks/useEventListener';
import {
  Activity,
  NotebookPen,
  BookOpen,
  UtensilsCrossed,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Play,
  Crown,
  FlaskConical,
  Stethoscope,
  FileText,
  HeartHandshake,
  MapPin,
  Star,
  Brain,
  Clock3,
  BookmarkX,
  History,
  Search,
  MessageSquare,
  Plus,
  MoreVertical,
  Trash2,
  Heart,
  BarChart3,
  LayoutDashboard,
  CalendarDays,
  Users,
  Leaf,
  User,
  Settings,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { cn } from 'lib/utils';
import { AppPage, EIRData, Filters, ChatThread } from 'types';
import { SubscriptionModal } from 'features/Subscription';
import { useSubscription } from 'hooks/useSubscription';
import { useThemeLogo } from 'hooks/useThemeLogo';
import { useOnboardingTarget } from 'context/OnboardingContext';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { getPathForPage } from 'utils/navigation';
import { generateUniqueId } from 'utils/id';
import { Input } from 'components/ui/input';
import { persistence } from 'services/persistence';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from 'components/ui/sidebar';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';

export interface MainSidebarProps {
  data: EIRData | null;
  filters: Filters;
  onFilterChange: (filters: Partial<Filters>) => void;
  onClearFilters: () => void;
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  onOpenSearch?: () => void;
  onOpenProfile?: () => void;
  betaFeaturesEnabled: boolean;
}

const THREAD_STORAGE_KEY = 'eir-chat-threads';
const PLACEHOLDER_TITLE = 'New chat';
const INITIAL_CHAT_VISIBLE_COUNT = 10;

const loadChatThreads = (): ChatThread[] => {
  const raw = persistence.getItem(THREAD_STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (thread): thread is ChatThread =>
          thread &&
          typeof thread === 'object' &&
          typeof thread.id === 'string' &&
          typeof thread.title === 'string' &&
          Array.isArray(thread.messages)
      )
      .sort((a, b) => {
        const aTime = new Date(a.updatedAt || a.createdAt).getTime();
        const bTime = new Date(b.updatedAt || b.createdAt).getTime();
        return bTime - aTime;
      });
  } catch {
    return [];
  }
};

// --- Sub-components for sidebar menu items with submenus ---

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future For You submenu
const ForYouMenuItem: React.FC<{
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
}> = ({ activePage, onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isForYouPage = activePage === 'for-you';
  const [open, setOpen] = useState(isForYouPage);

  useEffect(() => {
    setOpen(isForYouPage);
  }, [isForYouPage]);

  const currentSubPath = location.pathname.replace('/for-you/', '').replace('/for-you', '');

  const handleSubmenuClick = (subPath: string) => {
    navigate(`/for-you/${subPath}`);
    if (!isForYouPage) onNavigate('for-you');
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isForYouPage}
            tooltip="For You"
            onClick={() => {
              if (!isForYouPage) onNavigate('for-you');
            }}
          >
            <Star />
            <span>For You</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isForYouPage && (currentSubPath === 'your-day' || currentSubPath === '')}
                onClick={() => handleSubmenuClick('your-day')}
              >
                <Clock3 />
                <span>Your Day</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isForYouPage && currentSubPath === 'saved'}
                onClick={() => handleSubmenuClick('saved')}
              >
                <BookmarkX />
                <span>Saved Items</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isForYouPage && currentSubPath === 'history'}
                onClick={() => handleSubmenuClick('history')}
              >
                <History />
                <span>Your History</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future Health Assessment submenu
const HealthAssessmentMenuItem: React.FC<{
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  navRef?: React.Ref<HTMLLIElement>;
}> = ({ activePage, onNavigate, navRef }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAssessmentPage = activePage === 'health-assessment';
  const [open, setOpen] = useState(isAssessmentPage);

  useEffect(() => {
    setOpen(isAssessmentPage);
  }, [isAssessmentPage]);

  const currentSubPath = location.pathname.replace('/health-assessment/', '').replace('/health-assessment', '');

  const handleSubmenuClick = (subPath: string) => {
    navigate(`/health-assessment/${subPath}`);
    if (!isAssessmentPage) onNavigate('health-assessment');
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem ref={navRef}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isAssessmentPage}
            tooltip="Health Assessment"
            onClick={() => {
              if (!isAssessmentPage) onNavigate('health-assessment');
            }}
          >
            <Sparkles />
            <span>Health Assessment</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isAssessmentPage && (currentSubPath === '' || currentSubPath === 'health-assessment')}
                onClick={() => {
                  navigate('/health-assessment');
                  if (!isAssessmentPage) onNavigate('health-assessment');
                }}
              >
                <LayoutDashboard />
                <span>Overview</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isAssessmentPage && currentSubPath === 'tests'}
                onClick={() => handleSubmenuClick('tests')}
              >
                <FileText />
                <span>Self-Assessments</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isAssessmentPage && currentSubPath === 'ai'}
                onClick={() => handleSubmenuClick('ai')}
              >
                <Sparkles />
                <span>AI Assessment</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isAssessmentPage && currentSubPath === 'results'}
                onClick={() => handleSubmenuClick('results')}
              >
                <BarChart3 />
                <span>My Results</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

const GetCareMenuItem: React.FC<{
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
  navRef?: React.Ref<HTMLLIElement>;
}> = ({ activePage, onNavigate, navRef }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'health-map';
  const isGetCarePage = activePage === 'get-care';
  const [open, setOpen] = useState(isGetCarePage);

  useEffect(() => {
    setOpen(isGetCarePage);
  }, [isGetCarePage]);

  const handleSubmenuClick = (tab: string) => {
    navigate(`/get-care?tab=${tab}`);
    if (!isGetCarePage) onNavigate('get-care');
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem ref={navRef}>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isGetCarePage}
            tooltip="Get Care"
            onClick={() => {
              if (!isGetCarePage) onNavigate('get-care');
            }}
          >
            <HeartHandshake />
            <span>Get Care</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isGetCarePage && currentTab === 'health-map'}
                onClick={() => handleSubmenuClick('health-map')}
              >
                <MapPin />
                <span>Health Map</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={isGetCarePage && currentTab === 'request-care'}
                onClick={() => handleSubmenuClick('request-care')}
              >
                <FileText />
                <span>Request Care</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};


type ChatGroup = { label: string; chats: ChatThread[] };

const groupChatsByDate = (chats: ChatThread[]): ChatGroup[] => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86_400_000;
  const startOfLastWeek = startOfToday - 7 * 86_400_000;

  const buckets: Record<string, ChatThread[]> = {
    Today: [],
    Yesterday: [],
    'This week': [],
    Earlier: [],
  };

  for (const chat of chats) {
    const t = new Date(chat.updatedAt || chat.createdAt).getTime();
    if (t >= startOfToday) buckets['Today'].push(chat);
    else if (t >= startOfYesterday) buckets['Yesterday'].push(chat);
    else if (t >= startOfLastWeek) buckets['This week'].push(chat);
    else buckets['Earlier'].push(chat);
  }

  return Object.entries(buckets)
    .filter(([, c]) => c.length > 0)
    .map(([label, c]) => ({ label, chats: c }));
};

const YourChatsMenuItem: React.FC<{
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
}> = ({ activePage, onNavigate }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeThreadId = searchParams.get('threadId');
  const [chats, setChats] = useState<ChatThread[]>(() => loadChatThreads());
  const isChatPage = activePage === 'eir-chat';
  const [open, setOpen] = useState(false);

  const loadChats = () => setChats(loadChatThreads());
  usePolling(loadChats, 1000);
  useEventListener('storage', (event: StorageEvent) => {
    if (event.key === THREAD_STORAGE_KEY) loadChats();
  });

  const chatsWithMessages = useMemo(
    () => chats.filter((chat) => (chat.messages && chat.messages.length > 0) || chat.programId),
    [chats]
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_CHAT_VISIBLE_COUNT);
  const activeIndex = chatsWithMessages.findIndex((c) => c.id === activeThreadId);
  const effectiveVisibleCount =
    activeIndex >= 0 && activeIndex >= visibleCount ? activeIndex + 1 : visibleCount;
  const visibleChats = chatsWithMessages.slice(0, effectiveVisibleCount);
  const hasOlderChats = chatsWithMessages.length > effectiveVisibleCount;

  useEffect(() => {
    setOpen(isChatPage && chatsWithMessages.length > 0);
  }, [isChatPage, chatsWithMessages.length]);

  const handleChatClick = (threadId: string) => {
    const updatedChats = chats.map((chat) =>
      chat.id === threadId ? { ...chat, isNew: false } : chat
    );
    persistence.setJSON(THREAD_STORAGE_KEY, updatedChats);
    setChats(updatedChats);
    navigate(`${getPathForPage('eir-chat')}?threadId=${threadId}`);
    if (!isChatPage) onNavigate('eir-chat');
  };

  const handleDeleteChat = (threadId: string) => {
    const updatedChats = chats.filter((chat) => chat.id !== threadId);
    persistence.setJSON(THREAD_STORAGE_KEY, updatedChats);
    setChats(updatedChats);
    if (activeThreadId === threadId) {
      navigate(getPathForPage('eir-chat'));
    }
  };

  if (chatsWithMessages.length === 0 && !isChatPage) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip="Your Chats"
            onClick={() => {
              if (!isChatPage && chatsWithMessages.length > 0) {
                navigate(`${getPathForPage('eir-chat')}?threadId=${chatsWithMessages[0].id}`);
              } else if (!isChatPage) {
                onNavigate('eir-chat');
              }
            }}
          >
            <MessageSquare />
            <span>Your Chats</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {groupChatsByDate(visibleChats).map((group) => (
              <React.Fragment key={group.label}>
                <SidebarMenuSubItem>
                  <span className="px-2 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground select-none block">
                    {group.label}
                  </span>
                </SidebarMenuSubItem>
                {group.chats.map((chat) => {
                  let ChatIcon = MessageSquare;
                  if (chat.journalNotesThreadId === 'journal-notes') ChatIcon = NotebookPen;
                  else if (chat.programId) ChatIcon = Brain;
                  const isActive = activeThreadId === chat.id;
                  return (
                    <SidebarMenuSubItem key={chat.id} className="group/chat relative">
                      <SidebarMenuSubButton
                        isActive={isActive}
                        onClick={() => handleChatClick(chat.id)}
                        className="pr-8"
                      >
                        <ChatIcon />
                        <span className="truncate">{chat.title || PLACEHOLDER_TITLE}</span>
                        {chat.isNew && (
                          <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-emerald-500 animate-pulse" />
                        )}
                      </SidebarMenuSubButton>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={cn(
                              'absolute right-1 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-md opacity-0 transition-opacity group-hover/chat:opacity-100 focus:opacity-100',
                              'hover:bg-sidebar-accent text-sidebar-foreground'
                            )}
                            aria-label="Chat options"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start">
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDeleteChat(chat.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete chat
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuSubItem>
                  );
                })}
              </React.Fragment>
            ))}
            {hasOlderChats && (
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  onClick={() => setVisibleCount(chatsWithMessages.length)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <ChevronDown className="h-4 w-4" />
                  <span>View older chats ({chatsWithMessages.length - effectiveVisibleCount} more)</span>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            )}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

const WELLNESS_PAGES: AppPage[] = [
  'my-health-mental',
  'my-health-physical',
  'my-health-emotional',
  'my-health-social',
];

const WellnessMenuItem: React.FC<{
  activePage: AppPage;
  onNavigate: (page: AppPage) => void;
}> = ({ activePage, onNavigate }) => {
  const isWellnessActive = WELLNESS_PAGES.includes(activePage);
  const [open, setOpen] = useState(isWellnessActive);

  useEffect(() => {
    setOpen(isWellnessActive);
  }, [isWellnessActive]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="group/wellness">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            isActive={isWellnessActive}
            tooltip="Wellness"
          >
            <Leaf />
            <span>Wellness</span>
            <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/wellness:rotate-90" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={activePage === 'my-health-mental'}
                onClick={() => onNavigate('my-health-mental')}
              >
                <Brain />
                <span>Mental</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={activePage === 'my-health-physical'}
                onClick={() => onNavigate('my-health-physical')}
              >
                <Heart />
                <span>Physical</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={activePage === 'my-health-emotional'}
                onClick={() => onNavigate('my-health-emotional')}
              >
                <HeartHandshake />
                <span>Emotional</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
            <SidebarMenuSubItem>
              <SidebarMenuSubButton
                isActive={activePage === 'my-health-social'}
                onClick={() => onNavigate('my-health-social')}
              >
                <Users />
                <span>Social</span>
              </SidebarMenuSubButton>
            </SidebarMenuSubItem>
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

const loadProfileName = (): string => {
  try {
    const raw = persistence.getItem('eir-user-profile');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return typeof parsed?.name === 'string' ? parsed.name : '';
  } catch {
    return '';
  }
};

export const MainSidebar: React.FC<MainSidebarProps> = (props) => {
  const {
    data,
    filters,
    onFilterChange,
    onClearFilters,
    activePage,
    onNavigate,
    onOpenSearch,
    onOpenProfile,
    betaFeaturesEnabled,
  } = props;

  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [profileName, setProfileName] = useState<string>(loadProfileName);
  const { isPremium, subscription } = useSubscription();

  useEventListener('profileUpdated', () => setProfileName(loadProfileName()));
  const navigate = useNavigate();
  const logoSrc = useThemeLogo();

  const trackHealthNavRef = useOnboardingTarget('nav-track-health');
  const knowHealthNavRef = useOnboardingTarget('nav-know-health');
  const growSelfNavRef = useOnboardingTarget('nav-grow-self');
  const getCareNavRef = useOnboardingTarget('nav-get-care');
  const eirDocsNavRef = useOnboardingTarget('nav-eir-docs');

  const categories = data
    ? Array.from(new Set(data.entries.map((entry) => entry.category).filter(Boolean)))
    : [];
  const providers = data
    ? Array.from(new Set(data.entries.map((entry) => entry.provider?.name).filter(Boolean)))
    : [];
  const statuses = data
    ? Array.from(new Set(data.entries.map((entry) => entry.status).filter(Boolean)))
    : [];

  const handleNewChat = () => {
    const now = new Date().toISOString();
    const newThread: ChatThread = {
      id: generateUniqueId('chat-thread'),
      title: PLACEHOLDER_TITLE,
      createdAt: now,
      updatedAt: now,
      messages: [],
      context: {
        clinicalMode: 'none',
        clinicalEntryIds: [],
        journalMode: 'none',
        journalNoteIds: [],
        nutritionMode: 'none',
        nutritionEntryIds: [],
        summaryMode: 'none',
        summaryEntryIds: [],
      },
    };
    const existingThreads = loadChatThreads();
    const updatedThreads = [newThread, ...existingThreads];
    persistence.setJSON(THREAD_STORAGE_KEY, updatedThreads);
    navigate(`${getPathForPage('eir-chat')}?threadId=${newThread.id}`);
  };

  return (
    <>
      <Sidebar collapsible="icon" className="border-r">
        {/* Header: Logo + Search */}
        <SidebarHeader>
          <div className="flex items-center gap-3">
            <img
              src={logoSrc}
              alt="EIR"
              className="h-8 w-8 shrink-0 cursor-pointer object-contain"
              onClick={() => onNavigate('ehr')}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            {!collapsed && onOpenSearch && (
              <Button
                variant="outline"
                size="sm"
                className="flex-1 justify-start gap-2 text-muted-foreground"
                onClick={onOpenSearch}
              >
                <Search className="h-4 w-4" />
                <span>Search Eir</span>
              </Button>
            )}
          </div>
        </SidebarHeader>

        <SidebarContent>
          {/* Chat section */}
          <SidebarGroup>
            <SidebarGroupLabel>Chat</SidebarGroupLabel>
            <SidebarGroupContent className="group-data-[collapsible=icon]:hidden">
              <Button className="w-full justify-start gap-2" onClick={handleNewChat}>
                <Plus className="h-4 w-4" />
                New Chat
              </Button>
            </SidebarGroupContent>
            <SidebarMenu>
              <YourChatsMenuItem activePage={activePage} onNavigate={onNavigate} />
            </SidebarMenu>
          </SidebarGroup>

          {/* Today section */}
          <SidebarGroup>
            <SidebarGroupLabel>Today</SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem ref={trackHealthNavRef}>
                <SidebarMenuButton
                  isActive={activePage === 'my-health'}
                  tooltip="Dashboard"
                  onClick={() => onNavigate('my-health')}
                >
                  <LayoutDashboard />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activePage === 'journal'}
                  tooltip="Journal"
                  onClick={() => onNavigate('journal')}
                >
                  <NotebookPen />
                  <span>Journal</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>

          {/* Health section */}
          <SidebarGroup>
            <SidebarGroupLabel>Health</SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem ref={knowHealthNavRef}>
                <SidebarMenuButton
                  isActive={activePage === 'ehr' || activePage === 'health-records'}
                  tooltip="Health Records"
                  onClick={() => onNavigate('health-records')}
                >
                  <FileText />
                  <span>Health Records</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <HealthAssessmentMenuItem activePage={activePage} onNavigate={onNavigate} />

              <SidebarMenuItem ref={growSelfNavRef}>
                <SidebarMenuButton
                  isActive={activePage === 'calendar'}
                  tooltip="Calendar"
                  onClick={() => onNavigate('calendar')}
                >
                  <CalendarDays />
                  <span>Calendar</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activePage === 'tracking'}
                  tooltip="Tracking"
                  onClick={() => onNavigate('tracking')}
                >
                  <Activity />
                  <span>Tracking</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>

          {/* Grow section */}
          <SidebarGroup>
            <SidebarGroupLabel>Grow</SidebarGroupLabel>
            <SidebarMenu>
              <WellnessMenuItem activePage={activePage} onNavigate={onNavigate} />

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activePage === 'cbt-programs' || activePage === 'health-programs'}
                  tooltip="Programs"
                  onClick={() => onNavigate('health-programs')}
                >
                  <Brain />
                  <span>Programs</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activePage === 'skills'}
                  tooltip="Skills Library"
                  onClick={() => onNavigate('skills')}
                >
                  <Sparkles />
                  <span>Skills Library</span>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {(betaFeaturesEnabled || activePage === 'nutrition') && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={activePage === 'nutrition'}
                    tooltip="Nutrition"
                    onClick={() => onNavigate('nutrition')}
                  >
                    <UtensilsCrossed />
                    <span>Nutrition</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">
                      <FlaskConical className="mr-0.5 h-3 w-3" />
                      Beta
                    </Badge>
                  </SidebarMenuBadge>
                </SidebarMenuItem>
              )}

              {(betaFeaturesEnabled || activePage === 'health-literacy') && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={activePage === 'health-literacy'}
                    tooltip="Health Literacy"
                    onClick={() => onNavigate('health-literacy')}
                  >
                    <BookOpen />
                    <span>Health Literacy</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">
                      <FlaskConical className="mr-0.5 h-3 w-3" />
                      Beta
                    </Badge>
                  </SidebarMenuBadge>
                </SidebarMenuItem>
              )}

              {(betaFeaturesEnabled || activePage === 'health-videos') && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={activePage === 'health-videos'}
                    tooltip="Health Videos"
                    onClick={() => onNavigate('health-videos')}
                  >
                    <Play />
                    <span>Health Videos</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">
                      <FlaskConical className="mr-0.5 h-3 w-3" />
                      Beta
                    </Badge>
                  </SidebarMenuBadge>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroup>

          {/* Care section — note: Patient section removed; info is on Health Records page */}
          <SidebarGroup>
            <SidebarGroupLabel>Care</SidebarGroupLabel>
            <SidebarMenu>
              <GetCareMenuItem activePage={activePage} onNavigate={onNavigate} navRef={getCareNavRef} />

              {(betaFeaturesEnabled || activePage === 'care-coach') && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={activePage === 'care-coach'}
                    tooltip="Care Coach"
                    onClick={() => onNavigate('care-coach')}
                  >
                    <Stethoscope />
                    <span>Care Coach</span>
                  </SidebarMenuButton>
                  <SidebarMenuBadge>
                    <Badge variant="muted" className="h-5 px-1.5 text-[10px]">
                      <FlaskConical className="mr-0.5 h-3 w-3" />
                      Beta
                    </Badge>
                  </SidebarMenuBadge>
                </SidebarMenuItem>
              )}

              <SidebarMenuItem ref={eirDocsNavRef}>
                <SidebarMenuButton
                  isActive={activePage === 'eir-docs'}
                  tooltip="Eir Docs"
                  onClick={() => onNavigate('eir-docs')}
                >
                  <BookOpen />
                  <span>Eir Docs</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>

          {/* Filters (EHR page only) */}
          {activePage === 'ehr' && (
            <SidebarGroup className="group-data-[collapsible=icon]:hidden">
              <SidebarGroupLabel>Filters</SidebarGroupLabel>
              <SidebarGroupContent className="space-y-3 px-2">
                <div className="space-y-1.5">
                  <label htmlFor="filter-category" className="text-xs font-medium">
                    Category
                  </label>
                  <select
                    id="filter-category"
                    value={filters.category}
                    onChange={(e) => onFilterChange({ category: e.target.value })}
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="filter-provider" className="text-xs font-medium">
                    Provider
                  </label>
                  <select
                    id="filter-provider"
                    value={filters.provider}
                    onChange={(e) => onFilterChange({ provider: e.target.value })}
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                  >
                    <option value="">All Providers</option>
                    {providers.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="filter-status" className="text-xs font-medium">
                    Status
                  </label>
                  <select
                    id="filter-status"
                    value={filters.status}
                    onChange={(e) => onFilterChange({ status: e.target.value })}
                    className="h-8 w-full rounded-md border bg-background px-2 text-sm"
                  >
                    <option value="">All Statuses</option>
                    {statuses.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="filter-start-date" className="text-xs font-medium">
                    Date range
                  </label>
                  <Input
                    id="filter-start-date"
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => onFilterChange({ startDate: e.target.value })}
                    className="h-8"
                  />
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => onFilterChange({ endDate: e.target.value })}
                    className="h-8"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="filter-search" className="text-xs font-medium">
                    Search
                  </label>
                  <SidebarInput
                    id="filter-search"
                    type="search"
                    placeholder="Search notes..."
                    value={filters.searchTerm}
                    onChange={(e) => onFilterChange({ searchTerm: e.target.value })}
                  />
                </div>

                <Button size="sm" variant="outline" className="w-full" onClick={onClearFilters}>
                  Clear Filters
                </Button>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>

        {/* Footer: Local Health Profile + Premium status */}
        <SidebarFooter>
          <SidebarMenu>
            {/* Profile row */}
            <SidebarMenuItem>
              <SidebarMenuButton
                size="lg"
                tooltip="Your Local Health Profile"
                onClick={onOpenProfile}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent">
                  <User className="h-4 w-4" />
                </div>
                <div className="flex flex-col text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                  <span className="truncate font-semibold">Your Local Health Profile</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {profileName || 'Set up your profile'}
                  </span>
                </div>
              </SidebarMenuButton>
              <SidebarMenuAction showOnHover onClick={onOpenProfile} title="Configure profile">
                <Settings className="h-4 w-4" />
              </SidebarMenuAction>
            </SidebarMenuItem>

            {/* Upgrade row — non-premium only */}
            {!isPremium && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  tooltip="Upgrade to Premium"
                  onClick={() => setShowSubscriptionModal(true)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Crown className="h-4 w-4 shrink-0 text-amber-500" />
                  <span>Upgrade to Premium</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {/* Premium status row */}
            {isPremium && subscription && (
              <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
                <div className="flex items-center gap-2 px-2 py-1.5">
                  <Crown className="h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">
                    {subscription.plan === 'monthly' ? 'Monthly Plan' : 'Annual Plan'}
                  </span>
                </div>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarFooter>

        <SidebarRail />
      </Sidebar>

      {!isPremium && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
        />
      )}
    </>
  );
};
